import { FunctionComponent } from 'react'
import { CreationToolSourceFile } from '.'
import {
    CaptionEditorIcon,
    CropEditorIcon,
    EditorIcon,
    FilterEditorIcon,
    GradientIconProps,
    LayerEditorIcon,
    StickerEditorIcon,
    WatermarkEditorIcon,
} from '../components/icons'
import { MAX_OUTPUT_GIF_DURATION_FOR_CAPTIONS } from '../constants'
import CreationTool from './creation-tool'
import CreationToolGradient from './gradient'
import CreationToolOutputType from './output-type'

/**
 * Creation tool source editor sections.
 */
enum CreationToolSourceEditor {
    Edit = 'CT_SOURCE_EDITOR_EDIT',
    Caption = 'CT_SOURCE_EDITOR_CAPTION',
    Crop = 'CT_SOURCE_EDITOR_CROP',
    Filters = 'CT_SOURCE_EDITOR_FILTERS',
    Stickers = 'CT_SOURCE_EDITOR_STICKERS',
    Watermark = 'CT_SOURCE_EDITOR_WATERMARK',
    Layers = 'CT_SOURCE_EDITOR_LAYERS',
}

namespace CreationToolSourceEditor {
    /**
     * Returns all source editors. (in order)
     */
    export function all(): CreationToolSourceEditor[] {
        return [
            CreationToolSourceEditor.Edit,
            CreationToolSourceEditor.Caption,
            // CreationToolSourceEditor.Filters,
            // CreationToolSourceEditor.Stickers,
            CreationToolSourceEditor.Crop,
            // CreationToolSourceEditor.Watermark,
            CreationToolSourceEditor.Layers,
        ]
    }

    /**
     * Returns the source editor's title.
     */
    export function title(editor: CreationToolSourceEditor): string {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return 'Edit'
            case CreationToolSourceEditor.Caption:
                return 'Caption'
            case CreationToolSourceEditor.Crop:
                return 'Crop'
            case CreationToolSourceEditor.Filters:
                return 'Filters'
            case CreationToolSourceEditor.Stickers:
                return 'Stickers'
            case CreationToolSourceEditor.Watermark:
                return 'Watermark'
            case CreationToolSourceEditor.Layers:
                return 'Layers'
        }
    }

    /**
     * Returns the source editor's menu item title.
     */
    export function menuTitle(editor: CreationToolSourceEditor): string {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return 'Open Editor'
            case CreationToolSourceEditor.Caption:
                return 'Add Caption'
            case CreationToolSourceEditor.Crop:
                return 'Crop'
            case CreationToolSourceEditor.Filters:
                return 'Add Filters'
            case CreationToolSourceEditor.Stickers:
                return 'Add Stickers'
            case CreationToolSourceEditor.Watermark:
                return 'Watermark'
            case CreationToolSourceEditor.Layers:
                return 'Layers'
        }
    }

    /**
     * Returns the source editor's gradient.
     */
    export function gradient(editor: CreationToolSourceEditor): CreationToolGradient {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return CreationToolGradient.RedYellow
            case CreationToolSourceEditor.Caption:
                return CreationToolGradient.RedPink
            case CreationToolSourceEditor.Crop:
                return CreationToolGradient.IndigoBlue
            case CreationToolSourceEditor.Filters:
                return CreationToolGradient.IndigoPurple
            case CreationToolSourceEditor.Stickers:
                return CreationToolGradient.BlueGreen
            case CreationToolSourceEditor.Watermark:
                return CreationToolGradient.IndigoPurple
            case CreationToolSourceEditor.Layers:
                return CreationToolGradient.RedYellow
        }
    }

    /**
     * Returns the editor's supported output types.
     */
    export function supportedOutputTypes(editor: CreationToolSourceEditor): CreationToolOutputType[] {
        switch (editor) {
            case CreationToolSourceEditor.Layers:
            case CreationToolSourceEditor.Filters:
            case CreationToolSourceEditor.Stickers:
                return [CreationToolOutputType.Gif, CreationToolOutputType.Sticker]
            default:
                return CreationToolOutputType.all()
        }
    }

    /**
     * Returns the tool's that the source support.
     */
    export function supportedToolsForSource(source: CreationToolSourceFile): CreationToolSourceEditor[] {
        const tools = CreationToolSourceEditor.all()

        if (source.duration > MAX_OUTPUT_GIF_DURATION_FOR_CAPTIONS) {
            return tools.filter((tool) => tool !== CreationToolSourceEditor.Layers)
        }

        return tools
    }

    /**
     * Returns the editor's supported creation tools.
     */
    export function supportedCreationTools(editor: CreationToolSourceEditor): CreationTool[] {
        switch (editor) {
            case CreationToolSourceEditor.Watermark:
                return [CreationTool.Upload]
            default:
                return CreationTool.all()
        }
    }

    /**
     * Returns whether or not the editor requires the preview's playhead time.
     */
    export function requiresPreviewPlayheadTime(editor: CreationToolSourceEditor): boolean {
        switch (editor) {
            default:
                return true
        }
    }

    /**
     * Returns the editor's icon react component.
     */
    export function icon(editor: CreationToolSourceEditor): FunctionComponent<GradientIconProps> {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return EditorIcon
            case CreationToolSourceEditor.Caption:
                return CaptionEditorIcon
            case CreationToolSourceEditor.Crop:
                return CropEditorIcon
            case CreationToolSourceEditor.Filters:
                return FilterEditorIcon
            case CreationToolSourceEditor.Stickers:
                return StickerEditorIcon
            case CreationToolSourceEditor.Watermark:
                return WatermarkEditorIcon
            case CreationToolSourceEditor.Layers:
                return LayerEditorIcon
        }
    }

    /**
     * Returns the editor's GA label
     */
    export function gaLabel(editor: CreationToolSourceEditor): string {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return 'edit'
            case CreationToolSourceEditor.Caption:
                return 'caption'
            case CreationToolSourceEditor.Crop:
                return 'crop'
            case CreationToolSourceEditor.Filters:
                return 'filters'
            case CreationToolSourceEditor.Stickers:
                return 'stickers'
            case CreationToolSourceEditor.Watermark:
                return 'watermark'
            case CreationToolSourceEditor.Layers:
                return 'layers'
        }
    }
}

export default CreationToolSourceEditor
